import { useEffect, useRef, useState } from "react";

/**
 * Barcode scanning through the phone camera.
 *
 * Chrome on Android has BarcodeDetector built in, so this needs no library.
 * Anywhere it is missing — desktop Firefox, older browsers — the number can be
 * typed instead, which is all the lookup needs.
 */
export function BarcodeScanner({
  onCode,
  onClose,
}: {
  onCode: (code: string) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [err, setErr] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const supported = typeof window !== "undefined" && "BarcodeDetector" in window;

  useEffect(() => {
    if (!supported) return;
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let stopped = false;

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (stopped) return;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        const Detector = (window as unknown as {
          BarcodeDetector: new (o: { formats: string[] }) => { detect: (s: CanvasImageSource) => Promise<{ rawValue: string }[]> };
        }).BarcodeDetector;
        const detector = new Detector({
          formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128"],
        });
        const tick = async () => {
          if (stopped || !videoRef.current) return;
          try {
            const hits = await detector.detect(videoRef.current);
            const code = hits.find((h) => /^\d{6,14}$/.test(h.rawValue))?.rawValue;
            if (code) {
              onCode(code);
              return;
            }
          } catch {
            // a frame that cannot be decoded is normal; keep looking
          }
          timer = window.setTimeout(tick, 400);
        };
        timer = window.setTimeout(tick, 400);
      } catch (e) {
        setErr((e as Error).message || "No camera available");
      }
    })();

    return () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [supported, onCode]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/60 p-4 sm:items-center" onClick={onClose}>
      <div className="card w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-baseline justify-between">
          <h2 className="section-title">Scan a barcode</h2>
          <button type="button" className="text-sm text-ink-muted" onClick={onClose}>
            Close
          </button>
        </div>

        {supported && !err ? (
          <div className="mt-3 overflow-hidden rounded-md border border-ink-line bg-ink">
            <video ref={videoRef} playsInline muted className="h-56 w-full object-cover" />
          </div>
        ) : (
          <p className="mt-3 text-sm text-ink-muted">
            {err ?? "This browser can't use the camera for barcodes."} Type the number under the
            barcode instead.
          </p>
        )}

        <div className="mt-3 flex gap-2">
          <input
            className="input flex-1"
            inputMode="numeric"
            placeholder="9310072020016"
            value={typed}
            onChange={(e) => setTyped(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && typed.length >= 6 && onCode(typed)}
          />
          <button
            type="button"
            className="btn-secondary shrink-0 px-4"
            disabled={typed.length < 6}
            onClick={() => onCode(typed)}
          >
            Look up
          </button>
        </div>
      </div>
    </div>
  );
}
